import { useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { rectIntersection } from '@dnd-kit/core'
import classNames from 'classnames'
import { useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { useScreen } from '../../../../hooks'
import { DragDropProvider } from '../../../DragDrop'
import { Field } from '../../../Field'
import { FormikField } from '../../../Formik'
import { EmailFormContext } from '../../EmailForm.context'
import {
  EmailRecipientsCollapsed,
  EmailRecipientsField,
  RecipientTag,
} from '../../EmailRecipientsField'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_BCC,
  EMAIL_CREATION_FORM_FIELD_NAME_CC,
  EMAIL_CREATION_FORM_FIELD_NAME_PRINTER,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
} from '../EmailCreationForm.constants'
import {
  useDragDropRecipients,
  useRecipientsFields,
} from '../EmailCreationForm.hooks'
import { getEmailFormFlattenRecipients } from '../EmailCreationForm.utils'

import {
  DROPPABLE_INDEX_BCC_FIELD,
  DROPPABLE_INDEX_CC_FIELD,
  DROPPABLE_INDEX_TO_FIELD,
} from './ToField.constants'
import { getRecipientsInputErrorProps } from './ToField.utils'

import * as styles from '../EmailCreationForm.module.css'

function ToField({
  accountsNoEmails,
  autoAddEnabled,
  blackList,
  duplicateOptionKeys = new Set(),
  fieldsProps = EMPTY_OBJECT,
  hideCcBcc,
  setCollapsedHeader,
  setMassMailing,
  onChangeAutoAddEnabled,
  onExpandSelectedRecipientEmails,
  ...restProps
}) {
  const { values } = useFormikContext()

  const { t } = useTranslation()

  const { isMobileScreenSize } = useScreen()

  const formContext = useContext(EmailFormContext)

  const {
    collapsed,
    handleExpand,
    handleShowBccField,
    handleShowCcField,
    initShowMoreValue,
    setInitShowMoreValue,
    showBccField,
    showCcField,
    validateEmailInput,
  } = useRecipientsFields({
    accountsNoEmails,
    ...restProps,
  })

  const errorItems = useMemo(
    () => [...accountsNoEmails, ...blackList],
    [accountsNoEmails, blackList]
  )

  const { errorDrop, handleDragEnd, handleDragOver } = useDragDropRecipients()

  const handleClickCollapsedField = useCallback(() => {
    setInitShowMoreValue(false)
    handleExpand()
    setCollapsedHeader(false)
  }, [handleExpand, setCollapsedHeader, setInitShowMoreValue])

  const handleFocusContainer = useCallback(() => {
    setCollapsedHeader(false)
  }, [setCollapsedHeader])

  const Tag =
    fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_TO]?.Tag ?? RecipientTag

  const renderOverlay = useCallback(
    (activeId, activeData) => {
      if (!activeData?.item) {
        return null
      }

      return (
        <Tag
          disabled
          className={styles.overlayTag}
          isOpen={activeData?.isOpen}
          item={activeData?.item}
          tooltip={errorDrop}
        />
      )
    },
    [errorDrop]
  )

  const fieldInputClassName = classNames(styles.fieldInput, {
    [styles.fieldInputMobile]: isMobileScreenSize,
  })

  return (
    <DragDropProvider
      collisionDetection={rectIntersection}
      overlayProps={{ style: { pointerEvents: 'none' } }}
      renderOverlay={renderOverlay}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      {collapsed ? (
        <Field
          fieldInputClassName={fieldInputClassName}
          label={`${t('Email.ToLabelKey')}:`}
          labelClassName={styles.fieldLabel}
          shouldAdaptToMobileScreen={isMobileScreenSize}
        >
          <EmailRecipientsCollapsed
            Tag={fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_TO]?.Tag}
            getFlattenRecipients={getEmailFormFlattenRecipients}
            onClick={handleClickCollapsedField}
          />
        </Field>
      ) : (
        <div
          className={classNames(styles.toFieldContainer, {
            [styles.toFieldContainerMobile]: isMobileScreenSize,
          })}
          onFocus={handleFocusContainer}
        >
          <FormikField
            {...fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_TO]}
            Component={EmailRecipientsField}
            autoAddEnabled={autoAddEnabled}
            droppableIndex={DROPPABLE_INDEX_TO_FIELD}
            errorItems={errorItems}
            errorOptionKeys={duplicateOptionKeys}
            fieldComponentProps={{
              fieldInputClassName: fieldInputClassName,
              isLoading:
                fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_TO]?.isLoading,
              label: `${t('Email.ToLabelKey')}:`,
              labelClassName: styles.fieldLabel,
              shouldAdaptToMobileScreen: isMobileScreenSize,
            }}
            getErrorProps={getRecipientsInputErrorProps}
            initShowMoreValue={initShowMoreValue}
            name={EMAIL_CREATION_FORM_FIELD_NAME_TO}
            placeholder={t('Email.ToPlaceholderKey')}
            printer={values[EMAIL_CREATION_FORM_FIELD_NAME_PRINTER]}
            viewBcc={!hideCcBcc && !formContext.blocked && !showBccField}
            viewCc={!hideCcBcc && !formContext.blocked && !showCcField}
            onChangeAutoAddEnabled={onChangeAutoAddEnabled}
            onClickBcc={handleShowBccField}
            onClickCc={handleShowCcField}
            onCloseModal={formContext.onCloseModal}
            onExpandSelectedRecipientEmails={onExpandSelectedRecipientEmails}
            onSetMassMailing={setMassMailing}
            onSetModal={formContext.setModal}
            onValidate={validateEmailInput}
          />
          {showCcField && !hideCcBcc ? (
            <FormikField
              {...fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_CC]}
              Component={EmailRecipientsField}
              droppableIndex={DROPPABLE_INDEX_CC_FIELD}
              errorItems={errorItems}
              errorOptionKeys={duplicateOptionKeys}
              fieldComponentProps={{
                fieldInputClassName: fieldInputClassName,
                label: `${t('Email.CcLabelKey')}:`,
                labelClassName: styles.fieldLabel,
                shouldAdaptToMobileScreen: isMobileScreenSize,
              }}
              getErrorProps={getRecipientsInputErrorProps}
              name={EMAIL_CREATION_FORM_FIELD_NAME_CC}
              placeholder={t('Email.CcPlaceholderKey')}
              printer={values[EMAIL_CREATION_FORM_FIELD_NAME_PRINTER]}
              onCloseModal={formContext.onCloseModal}
              onSetMassMailing={setMassMailing}
              onSetModal={formContext.setModal}
              onValidate={validateEmailInput}
            />
          ) : null}
          {showBccField && !hideCcBcc ? (
            <FormikField
              {...fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_BCC]}
              Component={EmailRecipientsField}
              droppableIndex={DROPPABLE_INDEX_BCC_FIELD}
              errorItems={errorItems}
              errorOptionKeys={duplicateOptionKeys}
              fieldComponentProps={{
                fieldInputClassName: fieldInputClassName,
                label: `${t('Email.BccLabelKey')}:`,
                labelClassName: styles.fieldLabel,
                shouldAdaptToMobileScreen: isMobileScreenSize,
              }}
              getErrorProps={getRecipientsInputErrorProps}
              name={EMAIL_CREATION_FORM_FIELD_NAME_BCC}
              placeholder={t('Email.BccPlaceholderKey')}
              printer={values[EMAIL_CREATION_FORM_FIELD_NAME_PRINTER]}
              onCloseModal={formContext.onCloseModal}
              onSetMassMailing={setMassMailing}
              onSetModal={formContext.setModal}
              onValidate={validateEmailInput}
            />
          ) : null}
        </div>
      )}
    </DragDropProvider>
  )
}

export default ToField
